/* eslint-disable no-restricted-globals */
/* eslint-disable no-alert */
import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Row, Col, Card, CardBody, CardHeader, Modal, Label,
} from 'reactstrap';
import { withTranslation } from 'react-i18next';

import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import {
  Formik, Field, Form, ErrorMessage,
} from 'formik';
import * as Yup from 'yup';

import { get, post } from '../../helpers/api_helper';
import { Can } from '../../utilities/Can';
import BaseGrid from '../../components/Common/BaseGrid';

const RETURN_SERVICE_OPTIONS_BY_CARRIER = {
  UPS: [
    { value: '03', label: 'Ground' },
    { value: '02', label: '2 Day' },
    { value: '01', label: 'Air' },
  ],
  FEDEX: [
    { value: 'FEDEX_GROUND', label: 'Ground' },
    { value: 'FEDEX_2_DAY', label: '2 Day' },
    { value: 'FIRST_OVERNIGHT', label: 'Air' },
  ],
};

const SHIP_SERVICE_OPTIONS_BY_CARRIER = RETURN_SERVICE_OPTIONS_BY_CARRIER;

const EMPTY_APP_FORM = {
  site: '',
  tax_id: '',
  contact_firstname: '',
  contact_lastname: '',
  contact_email: '',
  contact_telephone: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  fedex_account: '',
  ups_account: '',
  return_carrier_code: 'UPS',
  return_service_code: '03',
  return_shipping_account_code: '',
  listing_channel: 'partner_api',
  search_engine: '',
  default_ship_service_carrier: 'UPS',
  default_ship_service_code: '',
  default_ship_service_flat_rate: '',
};

function normalizeAppFormValues(entity = {}) {
  const carrierCode = entity.return_carrier_code || EMPTY_APP_FORM.return_carrier_code;
  const defaultServiceCode = RETURN_SERVICE_OPTIONS_BY_CARRIER[carrierCode]?.[0]?.value || '';
  const defaultShipServiceCarrier = entity.default_ship_service_carrier
    || EMPTY_APP_FORM.default_ship_service_carrier;
  const defaultShipServiceCode = SHIP_SERVICE_OPTIONS_BY_CARRIER[
    defaultShipServiceCarrier
  ]?.[0]?.value || '';

  return {
    ...EMPTY_APP_FORM,
    ...entity,
    contact_telephone: entity.contact_telephone || '',
    return_carrier_code: carrierCode,
    return_service_code: entity.return_service_code || defaultServiceCode,
    return_shipping_account_code: entity.return_shipping_account_code || '',
    search_engine: entity.search_engine || '',
    default_ship_service_carrier: defaultShipServiceCarrier,
    default_ship_service_code: entity.default_ship_service_code || defaultShipServiceCode,
    default_ship_service_flat_rate: entity.default_ship_service_flat_rate ?? '',
  };
}

function PartnerApi(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const { t } = props;
  const [signal, setSignal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalClient, setModalClient] = useState(null);
  const [editingAppId, setEditingAppId] = useState(null);
  const [formInitialValues, setFormInitialValues] = useState(EMPTY_APP_FORM);

  const deleteApp = async (row) => {
    if (window.confirm(props.t('Are you sure you want to delete this app?'))) {
      await post(`/api/partner/app/${row.id}/delete`, {});
      setSignal(signal + 1);
    }
  };

  const createApp = async (values) => {
    try {
      const client = await post('/api/partner/app/create', values);
      setSignal(signal + 1);

      setModalClient(client);
    } catch (e) {
      toastr.error(e.message);
    }
  };

  const saveApp = async (id, values) => {
    try {
      await post(`/api/partner/app/save/${id}`, values);
      setSignal(signal + 1);
      setModalOpen(false);
      setEditingAppId(null);
      setFormInitialValues(EMPTY_APP_FORM);
      toastr.success(t('App updated successfully'));
    } catch (e) {
      toastr.error(e.message);
    }
  };

  const openCreateModal = () => {
    setEditingAppId(null);
    setModalClient(null);
    setFormInitialValues(EMPTY_APP_FORM);
    setModalOpen(true);
  };

  const editApp = async (row) => {
    try {
      const entity = await get(`/api/partner/app/${row.id}`);
      setEditingAppId(row.id);
      setModalClient(null);
      setFormInitialValues(normalizeAppFormValues(entity));
      setModalOpen(true);
    } catch (e) {
      toastr.error(e.message);
    }
  };

  const closeModal = () => {
    if (modalClient === null) {
      setEditingAppId(null);
      setFormInitialValues(EMPTY_APP_FORM);
      setModalOpen(false);
      return;
    }

    if (confirm(t('"I\'ve copied the details already"'))) {
      setEditingAppId(null);
      setModalClient(null);
      setFormInitialValues(EMPTY_APP_FORM);
      setModalOpen(false);
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col md="7">
            <Card>
              <CardHeader className="bg-transparent border-bottom">{props.t('Partner API Apps')}</CardHeader>
              <CardBody>
                <Can i="see" this="partner_api">
                  <BaseGrid
                    url="/api/partner/app"
                    shouldUpdate={signal}
                    create={openCreateModal}
                    config={
                      {
                        columns: {
                          site: {
                            label: t('Site'),
                            sort: true,
                            link: {
                              to: '/partner-api/%id%',
                            },
                          },
                          id: {
                            label: t('Client ID'),
                          },
                          actions: {
                            label: 'Actions',
                            actions: [
                              {
                                key: 'edit_app',
                                icon: 'mdi mdi-pencil',
                                class: 'btn btn-sm btn-soft-primary text-dark',
                                alt: t('Edit App'),
                                click: editApp,
                              },
                              {
                                key: 'remove_app',
                                icon: 'mdi mdi-close-thick',
                                class: 'btn btn-sm btn-soft-danger text-dark',
                                alt: t('Delete App'),
                                click: deleteApp,
                              },
                            ],
                          },
                        },
                        pagination: {
                          sort: 'id',
                          direction: 'desc',
                          perPage: 30,
                        },
                      }
                    }
                  />

                  <Modal
                    size="md"
                    isOpen={modalOpen}
                    backdrop="static"
                  >
                    <div className="modal-header">
                      <h5
                        className="modal-title mt-0"
                        id="myLargeModalLabel"
                      >
                        {editingAppId ? t('Edit Partner API App') : t('Partner API App')}
                      </h5>
                      <button
                        onClick={closeModal}
                        type="button"
                        className="close"
                        data-dismiss="modal"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                    <div className="modal-body">
                      {modalClient !== null && (
                        <>
                          <p>{t('Copy this down somewhere. It will no longer be visible after you close this.')}</p>
                          <div className="fs-4 mt-3 mb-3">
                            <strong className="fs-6">{t('Client ID')}</strong>
                            <pre>{modalClient.id}</pre>
                          </div>
                          <div className="fs-4 mt-3 mb-3">
                            <strong className="fs-6">{t('Client Secret')}</strong>
                            <pre>{modalClient.client_secret}</pre>
                          </div>
                        </>
                      )}
                      {modalClient === null && (
                        <Formik
                          enableReinitialize
                          initialValues={formInitialValues}
                          validationSchema={Yup.object().shape({
                            site: Yup.string().required(t('Site Label is required')),
                            tax_id: Yup.string(),
                            contact_firstname: Yup.string().required(t('Contact Name is required')),
                            contact_lastname: Yup.string().required(t('Contact Name is required')),
                            contact_email: Yup.string().email(t('Invalid email format')).required(t('Contact Email is required')),
                            contact_telephone: Yup.string().required(t('Contact telephone is required')),
                            address1: Yup.string().required(t('Billing Address is required')),
                            address2: Yup.string(), // Address Line 2 is often optional
                            city: Yup.string().required(t('City is required')),
                            state: Yup.string().required(t('State is required')),
                            zip: Yup.string().matches(/^\d{5}$/, t('Invalid Zip code format')).required(t('Zip is required')),
                            fedex_account: Yup.string(),
                            ups_account: Yup.string(),
                            return_carrier_code: Yup.string().oneOf(['UPS', 'FEDEX']).required(t('Return carrier is required')),
                            return_service_code: Yup.string()
                              .when('return_carrier_code', {
                                is: 'UPS',
                                then: Yup.string().oneOf(['03', '02', '01']).required(t('Return service is required')),
                              })
                              .when('return_carrier_code', {
                                is: 'FEDEX',
                                then: Yup.string().oneOf(['FEDEX_GROUND', 'FEDEX_2_DAY', 'FIRST_OVERNIGHT']).required(t('Return service is required')),
                              }),
                            return_shipping_account_code: Yup.string(),
                            listing_channel: Yup.string().required(),
                            search_engine: Yup.string().oneOf(['', 'part_number']),
                            default_ship_service_carrier: Yup.string().oneOf(['UPS', 'FEDEX']),
                            default_ship_service_code: Yup.string(),
                            default_ship_service_flat_rate: Yup.string().matches(/^\d+(\.\d{1,2})?$/, {
                              message: t('Enter a valid price'),
                              excludeEmptyString: true,
                            }),
                          })}
                          onSubmit={(values) => {
                            if (editingAppId) {
                              saveApp(editingAppId, values);
                            } else {
                              createApp(values);
                            }
                          }}
                        >
                          {({
                            // eslint-disable-next-line no-unused-vars
                            touched, errors, setFieldValue, values,
                          }) => (
                            <Form id="AppForm" autoComplete="off">
                              <Row>
                                <Col sm="6">
                                  <div className="mb-2">
                                    <Label
                                      for="site"
                                      className="col-form-label"
                                    >
                                      {props.t('Label/Site')}
                                    </Label>
                                    <Field
                                      type="text"
                                      className={`bg-warning bg-soft form-control ${(errors.site && touched.site ? 'is-invalid' : '')}`}
                                      id="site"
                                      name="site"
                                      onChange={(e) => {
                                        setFieldValue('site', e.target.value);
                                      }}
                                    />
                                    <ErrorMessage
                                      name="site"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>
                                <Col sm="6">
                                  <div className="mb-2">
                                    <Label
                                      for="tax_id"
                                      className="col-form-label"
                                    >
                                      {props.t('Tax ID')}
                                    </Label>
                                    <Field
                                      type="text"
                                      className={`form-control ${(errors.tax_id && touched.tax_id ? 'is-invalid' : '')}`}
                                      id="tax_id"
                                      name="tax_id"
                                      onChange={(e) => {
                                        setFieldValue('tax_id', e.target.value);
                                      }}
                                    />
                                    <ErrorMessage
                                      name="tax_id"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>
                              </Row>

                              <Row>
                                <Col sm="6">
                                  <div className="mb-2">
                                    <Label
                                      for="contact_firstname"
                                      className="col-form-label"
                                    >
                                      {props.t('Contact First Name')}
                                    </Label>
                                    <Field
                                      type="text"
                                      className={`form-control ${(errors.contact_firstname && touched.contact_firstname ? 'is-invalid' : '')}`}
                                      id="contact_firstname"
                                      name="contact_firstname"
                                      onChange={(e) => {
                                        setFieldValue('contact_firstname', e.target.value);
                                      }}
                                    />
                                    <ErrorMessage
                                      name="contact_firstname"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>

                                <Col sm="6">
                                  <div className="mb-2">
                                    <Label
                                      for="contact_lastname"
                                      className="col-form-label"
                                    >
                                      {props.t('Contact Last Name')}
                                    </Label>
                                    <Field
                                      type="text"
                                      className={`form-control ${(errors.contact_lastname && touched.contact_lastname ? 'is-invalid' : '')}`}
                                      id="contact_lastname"
                                      name="contact_lastname"
                                      onChange={(e) => {
                                        setFieldValue('contact_lastname', e.target.value);
                                      }}
                                    />
                                    <ErrorMessage
                                      name="contact_lastname"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>
                              </Row>
                              <Row>
                                <Col sm="6">
                                  <div className="mb-2">
                                    <Label
                                      for="contact_email"
                                      className="col-form-label"
                                    >
                                      {props.t('Contact Email')}
                                    </Label>
                                    <Field
                                      type="text"
                                      className={`form-control ${(errors.contact_email && touched.contact_email ? 'is-invalid' : '')}`}
                                      id="contact_email"
                                      name="contact_email"
                                      onChange={(e) => {
                                        setFieldValue('contact_email', e.target.value);
                                      }}
                                    />
                                    <ErrorMessage
                                      name="contact_email"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>

                                <Col sm="6">
                                  <div className="mb-2">
                                    <Label
                                      for="contact_telephone"
                                      className="col-form-label"
                                    >
                                      {props.t('Contact Telephone')}
                                    </Label>
                                    <Field
                                      type="text"
                                      className={`form-control ${(errors.contact_telephone && touched.contact_telephone ? 'is-invalid' : '')}`}
                                      id="contact_telephone"
                                      name="contact_telephone"
                                      onChange={(e) => {
                                        setFieldValue('contact_telephone', e.target.value);
                                      }}
                                    />
                                    <ErrorMessage
                                      name="contact_telephone"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>
                              </Row>

                              <Row>
                                <Col sm="12">
                                  <div className="mb-2">
                                    <Label
                                      for="address_1"
                                      className="col-form-label"
                                    >
                                      {props.t('Billing Address')}
                                    </Label>
                                    <Field
                                      type="text"
                                      className={`form-control ${(errors.address1 && touched.address1 ? 'is-invalid' : '')}`}
                                      id="address_1"
                                      name="address1"
                                      placeholder={props.t('Address Line 1')}
                                      onChange={(e) => {
                                        setFieldValue('address1', e.target.value);
                                      }}
                                    />
                                    <ErrorMessage
                                      name="address1"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>

                                  <div className="mb-2">
                                    <Field
                                      type="text"
                                      className={`form-control ${(errors.address2 && touched.address2 ? 'is-invalid' : '')}`}
                                      id="address_2"
                                      name="address2"
                                      placeholder={props.t('Address Line 2')}
                                      onChange={(e) => {
                                        setFieldValue('address2', e.target.value);
                                      }}
                                    />
                                    <ErrorMessage
                                      name="address2"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>
                              </Row>

                              <Row>
                                <Col sm="12">
                                  <Row>
                                    <Col sm="5">
                                      <div className="mb-2">
                                        <Label
                                          for="city"
                                          className="col-form-label"
                                        >
                                          {props.t('City')}
                                        </Label>
                                        <Field
                                          type="text"
                                          className={`form-control ${(errors.city && touched.city ? 'is-invalid' : '')}`}
                                          id="city"
                                          name="city"
                                          onChange={(e) => {
                                            setFieldValue('city', e.target.value);
                                          }}
                                        />
                                        <ErrorMessage
                                          name="city"
                                          component="div"
                                          className="invalid-feedback"
                                        />
                                      </div>
                                    </Col>
                                    <Col sm="3">
                                      <div className="mb-3">
                                        <Label
                                          for="state"
                                          className="col-form-label"
                                        >
                                          {props.t('State')}
                                        </Label>
                                        <Field
                                          type="text"
                                          className={`form-control ${(errors.state && touched.state ? 'is-invalid' : '')}`}
                                          id="state"
                                          name="state"
                                          onChange={(e) => {
                                            setFieldValue('state', e.target.value);
                                          }}
                                        />
                                        <ErrorMessage
                                          name="state"
                                          component="div"
                                          className="invalid-feedback"
                                        />
                                      </div>
                                    </Col>
                                    <Col sm="4">
                                      <div className="mb-3">
                                        <Label
                                          for="zip"
                                          className="col-form-label"
                                        >
                                          {props.t('Zip')}
                                        </Label>
                                        <Field
                                          type="text"
                                          className={`form-control ${(errors.zip && touched.zip ? 'is-invalid' : '')}`}
                                          id="zip"
                                          name="zip"
                                          onChange={(e) => {
                                            setFieldValue('zip', e.target.value);
                                          }}
                                        />
                                        <ErrorMessage
                                          name="zip"
                                          component="div"
                                          className="invalid-feedback"
                                        />
                                      </div>
                                    </Col>
                                  </Row>
                                </Col>
                              </Row>

                              <Row>
                                <Col sm="6">
                                  <div className="mb-2">
                                    <Label
                                      for="fedex_account"
                                      className="col-form-label"
                                    >
                                      {props.t('Fedex Account')}
                                    </Label>
                                    <Field
                                      type="text"
                                      className={`form-control ${(errors.fedex_account && touched.fedex_account ? 'is-invalid' : '')}`}
                                      id="fedex_account"
                                      name="fedex_account"
                                      onChange={(e) => {
                                        setFieldValue('fedex_account', e.target.value);
                                      }}
                                    />
                                    <ErrorMessage
                                      name="fedex_account"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>
                                <Col sm="6">
                                  <div className="mb-2">
                                    <Label
                                      for="ups_account"
                                      className="col-form-label"
                                    >
                                      {props.t('UPS Account')}
                                    </Label>
                                    <Field
                                      type="text"
                                      className={`form-control ${(errors.ups_account && touched.ups_account ? 'is-invalid' : '')}`}
                                      id="ups_account"
                                      name="ups_account"
                                      onChange={(e) => {
                                        setFieldValue('ups_account', e.target.value);
                                      }}
                                    />
                                    <ErrorMessage
                                      name="ups_account"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>
                              </Row>

                              <div className="mt-2">
                                <p>{t('If both account IDs blank, it defaults to required rate selection from our accounts')}</p>
                              </div>

                              <Row>
                                <Col sm="4">
                                  <div className="mb-2">
                                    <Label
                                      for="default_ship_service_carrier"
                                      className="col-form-label"
                                    >
                                      {props.t('Default Ship Service Carrier')}
                                    </Label>
                                    <Field
                                      as="select"
                                      className={`form-control ${(errors.default_ship_service_carrier && touched.default_ship_service_carrier ? 'is-invalid' : '')}`}
                                      id="default_ship_service_carrier"
                                      name="default_ship_service_carrier"
                                      onChange={(e) => {
                                        const nextCarrier = e.target.value;
                                        const nextServiceCode = SHIP_SERVICE_OPTIONS_BY_CARRIER[nextCarrier]?.[0]?.value || '';
                                        setFieldValue('default_ship_service_carrier', nextCarrier);
                                        setFieldValue('default_ship_service_code', nextServiceCode);
                                      }}
                                    >
                                      <option value="UPS">UPS</option>
                                      <option value="FEDEX">FEDEX</option>
                                    </Field>
                                    <ErrorMessage
                                      name="default_ship_service_carrier"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>
                                <Col sm="4">
                                  <div className="mb-2">
                                    <Label
                                      for="default_ship_service_code"
                                      className="col-form-label"
                                    >
                                      {props.t('Default Ship Service Code')}
                                    </Label>
                                    <Field
                                      as="select"
                                      className={`form-control ${(errors.default_ship_service_code && touched.default_ship_service_code ? 'is-invalid' : '')}`}
                                      id="default_ship_service_code"
                                      name="default_ship_service_code"
                                      onChange={(e) => {
                                        setFieldValue('default_ship_service_code', e.target.value);
                                      }}
                                    >
                                      {(SHIP_SERVICE_OPTIONS_BY_CARRIER[
                                        values.default_ship_service_carrier
                                      ] || []).map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {props.t(option.label)}
                                        </option>
                                      ))}
                                    </Field>
                                    <ErrorMessage
                                      name="default_ship_service_code"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>
                                <Col sm="4">
                                  <div className="mb-2">
                                    <Label
                                      for="default_ship_service_flat_rate"
                                      className="col-form-label"
                                    >
                                      {props.t('Default Ship Service Flat Rate')}
                                    </Label>
                                    <div className="input-group">
                                      <div className="input-group-prepend">
                                        <span className="input-group-text">$</span>
                                      </div>
                                      <Field
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        className={`form-control ${(errors.default_ship_service_flat_rate && touched.default_ship_service_flat_rate ? 'is-invalid' : '')}`}
                                        id="default_ship_service_flat_rate"
                                        name="default_ship_service_flat_rate"
                                        onChange={(e) => {
                                          setFieldValue('default_ship_service_flat_rate', e.target.value);
                                        }}
                                      />
                                      <ErrorMessage
                                        name="default_ship_service_flat_rate"
                                        component="div"
                                        className="invalid-feedback"
                                      />
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                              <Row>
                                <Col sm="12">
                                  <div className="help-block mt-2 mb-3">
                                    {props.t('Only used if no service is sent. If a flat rate is provided, the quoted rate is ignored. If no shipping account is set, the quoted rate will be applied to the order total during order placement unless a flat rate exists.')}
                                  </div>
                                </Col>
                              </Row>

                              <Row>
                                <Col sm="4">
                                  <div className="mb-2">
                                    <Label
                                      for="return_carrier_code"
                                      className="col-form-label"
                                    >
                                      {props.t('Return Carrier')}
                                    </Label>
                                    <Field
                                      as="select"
                                      className={`form-control ${(errors.return_carrier_code && touched.return_carrier_code ? 'is-invalid' : '')}`}
                                      id="return_carrier_code"
                                      name="return_carrier_code"
                                      onChange={(e) => {
                                        const carrierCode = e.target.value;
                                        const nextServiceCode = RETURN_SERVICE_OPTIONS_BY_CARRIER[carrierCode]?.[0]?.value || '';
                                        setFieldValue('return_carrier_code', carrierCode);
                                        setFieldValue('return_service_code', nextServiceCode);
                                      }}
                                    >
                                      <option value="UPS">UPS</option>
                                      <option value="FEDEX">FedEx</option>
                                    </Field>
                                    <ErrorMessage
                                      name="return_carrier_code"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>
                                <Col sm="4">
                                  <div className="mb-2">
                                    <Label
                                      for="return_service_code"
                                      className="col-form-label"
                                    >
                                      {props.t('Return Service')}
                                    </Label>
                                    <Field
                                      as="select"
                                      className={`form-control ${(errors.return_service_code && touched.return_service_code ? 'is-invalid' : '')}`}
                                      id="return_service_code"
                                      name="return_service_code"
                                      onChange={(e) => {
                                        setFieldValue('return_service_code', e.target.value);
                                      }}
                                    >
                                      {(RETURN_SERVICE_OPTIONS_BY_CARRIER[
                                        values.return_carrier_code
                                      ] || []).map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {props.t(option.label)}
                                        </option>
                                      ))}
                                    </Field>
                                    <ErrorMessage
                                      name="return_service_code"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>
                                <Col sm="4">
                                  <div className="mb-2">
                                    <Label
                                      for="return_shipping_account_code"
                                      className="col-form-label"
                                    >
                                      {props.t('Return Ship Account')}
                                    </Label>
                                    <Field
                                      type="text"
                                      className={`form-control ${(errors.return_shipping_account_code && touched.return_shipping_account_code ? 'is-invalid' : '')}`}
                                      id="return_shipping_account_code"
                                      name="return_shipping_account_code"
                                      onChange={(e) => {
                                        setFieldValue('return_shipping_account_code', e.target.value);
                                      }}
                                    />
                                    <ErrorMessage
                                      name="return_shipping_account_code"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                    <div className="help-block mt-2">
                                      {t('(Ours if blank)')}
                                    </div>
                                  </div>
                                </Col>
                              </Row>

                              <Row>
                                <Col sm="6">
                                  <div className="mb-2">
                                    <Label
                                      for="listing_channel"
                                      className="col-form-label"
                                    >
                                      {props.t('Listing Channel')}
                                    </Label>
                                    <Field
                                      type="text"
                                      className={`form-control ${(errors.listing_channel && touched.listing_channel ? 'is-invalid' : '')}`}
                                      id="listing_channel"
                                      name="listing_channel"
                                      onChange={(e) => {
                                        setFieldValue('listing_channel', e.target.value);
                                      }}
                                    />
                                    <ErrorMessage
                                      name="listing_channel"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>
                                <Col sm="6">
                                  <div className="mb-2">
                                    <Label
                                      for="search_engine"
                                      className="col-form-label"
                                    >
                                      {props.t('Search Engine')}
                                    </Label>
                                    <Field
                                      as="select"
                                      className={`form-control ${(errors.search_engine && touched.search_engine ? 'is-invalid' : '')}`}
                                      id="search_engine"
                                      name="search_engine"
                                      onChange={(e) => {
                                        setFieldValue('search_engine', e.target.value);
                                      }}
                                    >
                                      <option value="">{props.t('Elastic Search')}</option>
                                      <option value="part_number">{props.t('Part Number')}</option>
                                    </Field>
                                    <ErrorMessage
                                      name="search_engine"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  </div>
                                </Col>
                              </Row>

                            </Form>
                          )}
                        </Formik>
                      )}
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        onClick={() => {
                          if (modalClient === null) {
                            setModalOpen(false);
                          }

                          if (modalClient !== null && confirm(t('"I\'ve copied the details already"'))) {
                            setModalClient(null);
                            setModalOpen(false);
                          }
                        }}
                        className="btn btn-soft-dark"
                        data-dismiss="modal"
                      >
                        {modalClient === null ? t('Cancel') : t('Close')}
                      </button>
                      {modalClient === null && (
                        <button
                          type="submit"
                          form="AppForm"
                          className="btn btn-primary"
                        >
                          {t('Create App')}
                        </button>
                      )}
                    </div>
                  </Modal>
                </Can>

                <Can not i="see" this="client_api"><p>{props.t('Unauthorized')}</p></Can>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

PartnerApi.propTypes = {
  // eslint-disable-next-line react/require-default-props
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(PartnerApi));
